// Metric math for the demo Lens. Every ad starts from base counts (spend,
// impressions, clicks, conversions…); rates are always derived from those
// counts, so a row, a merged group and a date-scaled row stay consistent
// (ROAS = value / spend, CPA = spend / conversions, and so on).
import { DEMO_SUM_METRIC_KEYS, DEMO_SCORE_METRIC_KEYS } from './lensDemoMetricsRegistry'

// Per-ad behaviour rates that can't be rebuilt from counts; merged spend-weighted
export const DEMO_AVERAGE_METRIC_KEYS = [
  'like_rate', 'share_ratio', 'see_more_rate', 'first_frame_retention',
  'video_p25_watch_rate', 'video_p50_watch_rate', 'video_p75_watch_rate', 'video_p100_watch_rate'
]

// Each Foreplay Score is the percentile of one underlying rate
const SCORE_SOURCES = {
  hook_score: 'hook_rate',
  watch_score: 'hold_rate',
  click_score: 'ctr',
  convert_score: 'roas',
  viral_engagement_score: 'engagement_rate'
}

function round (value, digits = 2) {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

const ratio = (numerator, denominator, multiplier = 1) => (denominator ? round((numerator / denominator) * multiplier, 2) : 0)

export function deriveRateMetrics (sums) {
  const {
    spend = 0, impressions = 0, reach = 0, clicks = 0, link_clicks: linkClicks = 0,
    outbound_clicks: outboundClicks = 0, engagements = 0, three_second_video_view: threeSecondViews = 0,
    thruplays = 0, total_conversions: conversions = 0, conversion_value: value = 0
  } = sums
  return {
    frequency: ratio(impressions, reach),
    cpm: ratio(spend, impressions, 1000),
    roas: ratio(value, spend),
    cpa: ratio(spend, conversions),
    aov: ratio(value, conversions),
    revenue_per_mille: ratio(value, impressions, 1000),
    purchases_per_mille: ratio(conversions, impressions, 1000),
    ctr: ratio(clicks, impressions, 100),
    cpc: ratio(spend, clicks),
    link_ctr: ratio(linkClicks, impressions, 100),
    link_cpc: ratio(spend, linkClicks),
    outbound_ctr: ratio(outboundClicks, impressions, 100),
    outbound_cpc: ratio(spend, outboundClicks),
    engagement_rate: ratio(engagements, impressions, 100),
    thumbstop_ratio: ratio(threeSecondViews, impressions, 100),
    hook_rate: ratio(threeSecondViews, impressions, 100),
    hold_rate: ratio(thruplays, impressions, 100),
    cost_per_thruplay: ratio(spend, thruplays),
    conversion_rate: ratio(conversions, linkClicks, 100)
  }
}

// One ad's metrics. `band` sets its conversion range and ROAS; video metrics
// are zero for non-video ads
export function buildAdMetrics (random, band, isVideo) {
  const between = (min, max) => min + (max - min) * random()
  const spend = round(between(1200, 24000), 2)
  const impressions = Math.round(between(18000, 420000))
  const reach = Math.round(impressions / between(1.2, 2.6))
  const clicks = Math.max(1, Math.round(impressions * between(0.85, 4.4) / 100))
  const linkClicks = Math.max(1, Math.round(clicks * between(0.55, 0.8)))
  const outboundClicks = Math.round(linkClicks * between(0.85, 0.97))
  const conversions = Math.max(1, Math.round(between(band.conversionsMin, band.conversionsMax)))
  const value = round(spend * between(band.roasMin, band.roasMax), 2)
  const engagements = Math.round(impressions * between(0.4, 3.2) / 100)
  const hookRate = isVideo ? between(14, 46) : 0
  const holdRate = isVideo ? between(0.25, 0.7) * hookRate : 0
  const p25 = isVideo ? between(40, 80) : 0
  const p50 = p25 * between(0.55, 0.8)
  const p75 = p50 * between(0.5, 0.8)

  const sums = {
    spend,
    impressions,
    reach,
    clicks,
    link_clicks: linkClicks,
    outbound_clicks: outboundClicks,
    engagements,
    three_second_video_view: Math.round(impressions * hookRate / 100),
    thruplays: Math.round(impressions * holdRate / 100),
    total_conversions: conversions,
    conversion_value: value
  }
  const averages = {
    like_rate: round(between(0.2, 1.8), 2),
    share_ratio: round(between(0.02, 0.4), 2),
    see_more_rate: round(between(1, 6), 2),
    first_frame_retention: isVideo ? round(between(55, 90), 2) : 0,
    video_p25_watch_rate: round(p25, 2),
    video_p50_watch_rate: round(p50, 2),
    video_p75_watch_rate: round(p75, 2),
    video_p100_watch_rate: round(p75 * between(0.4, 0.8), 2)
  }
  return { ...sums, ...averages, ...deriveRateMetrics(sums) }
}

// Scale counts (date range share, previous period); rates are re-derived
export function scaleMetrics (row, factor) {
  const sums = {}
  DEMO_SUM_METRIC_KEYS.forEach((key) => {
    const scaled = (Number(row[key]) || 0) * factor
    sums[key] = key === 'spend' || key === 'conversion_value' ? round(scaled, 2) : Math.round(scaled)
  })
  return { ...sums, ...deriveRateMetrics(sums) }
}

function spendWeightedAverage (rows, key) {
  const totalSpend = rows.reduce((acc, row) => acc + (Number(row.spend) || 0), 0)
  if (!totalSpend) return 0
  return round(rows.reduce((acc, row) => acc + (Number(row[key]) || 0) * (Number(row.spend) || 0), 0) / totalSpend, 2)
}

// Totals for a set of rows: summed counts, spend-weighted behaviour rates,
// and rates derived from the summed counts
export function aggregateMetrics (rows) {
  const sums = {}
  DEMO_SUM_METRIC_KEYS.forEach((key) => {
    sums[key] = round(rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0), 2)
  })
  const averages = {}
  DEMO_AVERAGE_METRIC_KEYS.forEach((key) => { averages[key] = spendWeightedAverage(rows, key) })
  const weighted = { ...averages, ...deriveRateMetrics(sums) }
  return { metrics: { ...sums, ...weighted }, sums, weighted }
}

// "% of" metrics relative to the report totals
export function applyRelativeMetrics (rows, totals) {
  return rows.map((row) => ({
    ...row,
    percent_of_spend: ratio(row.spend, totals.spend, 100),
    percent_of_purchase_value: ratio(row.conversion_value, totals.conversion_value, 100)
  }))
}

// Foreplay Scores: percentile (0-100) of each score's source rate among the rows
export function applyScores (rows) {
  if (!rows.length) return rows
  const percentiles = {}
  DEMO_SCORE_METRIC_KEYS.forEach((scoreKey) => {
    const source = SCORE_SOURCES[scoreKey]
    const sorted = rows.map((row) => Number(row[source]) || 0).sort((a, b) => a - b)
    percentiles[scoreKey] = (value) => {
      if (sorted.length === 1) return value ? 100 : 0
      const below = sorted.filter((entry) => entry < value).length
      return Math.round((below / (sorted.length - 1)) * 100)
    }
  })
  return rows.map((row) => {
    const scores = {}
    DEMO_SCORE_METRIC_KEYS.forEach((scoreKey) => {
      scores[scoreKey] = percentiles[scoreKey](Number(row[SCORE_SOURCES[scoreKey]]) || 0)
    })
    return { ...row, ...scores }
  })
}
